import { useEffect, useMemo, useState } from "react";

import {
  executeConsoleRequest,
  getSubjects,
  getTableMetadata,
  getTables,
} from "./api";
import { ENDPOINTS, GUIDE_URL, SUPPORT_EMAIL } from "./endpoints";
import {
  buildConsoleRequest,
  buildDataRequest,
  buildPlanRequest,
  initialSelections,
} from "./request";
import type {
  BuilderSelections,
  ConsoleRequest,
  Dimension,
  EndpointId,
  HttpMethod,
  MeasureGroup,
  OutputFormat,
  PeriodType,
  SubjectGroup,
  TableMetadata,
  TableSubject,
} from "./types";

const EMPTY_SELECTIONS: BuilderSelections = {
  measures: {},
  periods: {},
  hierarchyByDimension: {},
  hierarchyValues: {},
};

interface ConsoleResult {
  ok: boolean;
  status: number;
  statusText: string;
  body: string;
  requestId?: string;
}

export default function App() {
  const [endpoint, setEndpoint] = useState<EndpointId>("subjects");
  const [method, setMethod] = useState<HttpMethod>("GET");
  const [format, setFormat] = useState<OutputFormat>("json");
  const [subjectGroups, setSubjectGroups] = useState<SubjectGroup[]>([]);
  const [tableSubjects, setTableSubjects] = useState<TableSubject[]>([]);
  const [subjectId, setSubjectId] = useState("");
  const [tableId, setTableId] = useState("");
  const [metadata, setMetadata] = useState<TableMetadata | null>(null);
  const [selections, setSelections] = useState<BuilderSelections>(EMPTY_SELECTIONS);
  const [loading, setLoading] = useState<"subjects" | "tables" | "metadata" | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"plan" | "execute" | null>(null);
  const [result, setResult] = useState<ConsoleResult | null>(null);

  const selectedEndpoint = ENDPOINTS.find((item) => item.id === endpoint)!;
  const needsSubjects = endpoint !== "subjects";
  const needsTable = endpoint === "table" || endpoint === "data";

  useEffect(() => {
    if (!needsSubjects || subjectGroups.length > 0) return;
    const controller = new AbortController();
    setLoading("subjects");
    setSettingsError(null);
    getSubjects(controller.signal)
      .then(setSubjectGroups)
      .catch((cause: unknown) => handleLoadError(cause, setSettingsError))
      .finally(() => setLoading((current) => (current === "subjects" ? null : current)));
    return () => controller.abort();
  }, [needsSubjects, subjectGroups.length]);

  useEffect(() => {
    setTableSubjects([]);
    setTableId("");
    setMetadata(null);
    setSelections(EMPTY_SELECTIONS);
    if (!needsTable || !subjectId) return;

    const controller = new AbortController();
    setLoading("tables");
    setSettingsError(null);
    getTables(subjectId, controller.signal)
      .then(setTableSubjects)
      .catch((cause: unknown) => handleLoadError(cause, setSettingsError))
      .finally(() => setLoading((current) => (current === "tables" ? null : current)));
    return () => controller.abort();
  }, [needsTable, subjectId]);

  useEffect(() => {
    setMetadata(null);
    setSelections(EMPTY_SELECTIONS);
    if (endpoint !== "data" || !tableId) return;

    const controller = new AbortController();
    setLoading("metadata");
    setSettingsError(null);
    getTableMetadata(tableId, controller.signal)
      .then((value) => {
        setMetadata(value);
        setSelections(initialSelections(value));
      })
      .catch((cause: unknown) => handleLoadError(cause, setSettingsError))
      .finally(() => setLoading((current) => (current === "metadata" ? null : current)));
    return () => controller.abort();
  }, [endpoint, tableId]);

  const tables = useMemo(() => flattenTables(tableSubjects), [tableSubjects]);
  const dataRequest = useMemo(
    () =>
      metadata
        ? buildDataRequest(metadata, selections, format)
        : { mgroup: {}, period: {}, hierarchy: {}, format },
    [format, metadata, selections],
  );
  const request = useMemo(
    () =>
      buildConsoleRequest({
        endpoint,
        method,
        format,
        subjectId,
        tableId,
        data: dataRequest,
      }),
    [dataRequest, endpoint, format, method, subjectId, tableId],
  );
  const ready =
    !needsTable ||
    (Boolean(tableId) &&
      (endpoint !== "data" || Boolean(metadata)));
  useEffect(() => {
    setResult(null);
  }, [request]);

  function changeEndpoint(nextEndpoint: EndpointId) {
    const definition = ENDPOINTS.find((item) => item.id === nextEndpoint)!;
    setEndpoint(nextEndpoint);
    setMethod(definition.defaultMethod);
    setSettingsError(null);
  }

  // Both buttons send a request and show the API's raw response in step 4.
  async function runRequest(kind: "plan" | "execute", target: ConsoleRequest) {
    if (!ready) return;
    setBusy(kind);
    setResult(null);
    try {
      const response = await executeConsoleRequest(target);
      const responseText = await response.text();
      setResult({
        ok: response.ok,
        status: response.status,
        statusText: response.statusText || (response.ok ? "OK" : "Fejl"),
        body: prettyResponse(responseText, response.headers.get("content-type")),
        requestId: response.headers.get("x-request-id") ?? undefined,
      });
    } catch (cause) {
      setResult({
        ok: false,
        status: 0,
        statusText: "Konsolfejl",
        body: errorMessage(cause),
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="app-shell">
      <div className="red-line" />
      <header className="site-header">
        <div>
          <a
            className="star-logo"
            href="https://star.dk/"
            target="_blank"
            rel="noreferrer"
            aria-label="Gå til STARs hjemmeside"
          >
            <img
              src="/star-logo.svg"
              alt="Styrelsen for Arbejdsmarked og Rekruttering"
              height={34}
            />
          </a>
          <a
            href="https://www.jobindsats.dk/"
            target="_blank"
            rel="noreferrer"
            aria-label="Gå til Jobindsats"
          >
            <img src="/jobindsats-logo.svg" alt="Jobindsats" height={30} />
          </a>
        </div>
      </header>

      <main className="console">
        <div className="intro">
          <h1>API-konsol</h1>
          <p>
            Denne konsol lader dig bygge og afprøve kald til Jobindsats API v3 uden at
            skrive kode. Har du brug for en dybere forklaring af opbygningen af kald,
            dimensioner, hierarkier eller autentificering, kan du læse{" "}
            <a href={GUIDE_URL} target="_blank" rel="noreferrer">
              brugervejledningen til version 3
            </a>
            . Oplever du fejl eller mangler adgang, kan du skrive til{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </div>

        <Step number="1" title="Vælg API-kald">
          <label className="field endpoint-field">
            <span>API-kald</span>
            <select
              value={endpoint}
              onChange={(event) => changeEndpoint(event.target.value as EndpointId)}
            >
              {ENDPOINTS.map((item) => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
            </select>
          </label>
          <div className="endpoint-description">
            <code>{selectedEndpoint.path}</code>
            <span>{selectedEndpoint.description}</span>
          </div>
          <details className="endpoint-help">
            <summary>Om dette kald</summary>
            <div>{selectedEndpoint.help}</div>
          </details>
        </Step>

        <Step number="2" title="Vælg indstillinger">
          <div className="settings-grid">
            <label className="field">
              <span>HTTP-metode</span>
              <select value={method} onChange={(event) => setMethod(event.target.value as HttpMethod)}>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
              </select>
            </label>
            <label className="field">
              <span>Svarformat</span>
              <select value={format} onChange={(event) => setFormat(event.target.value as OutputFormat)}>
                <option value="json">JSON</option>
                <option value="csv">CSV</option>
              </select>
            </label>

            {needsSubjects && (
              <label className="field">
                <span>{needsTable ? "Emne" : "Emnefilter (valgfrit)"}</span>
                <select
                  value={subjectId}
                  onChange={(event) => {
                    setSubjectId(event.target.value);
                    setSettingsError(null);
                  }}
                  disabled={loading === "subjects"}
                >
                  <option value="">
                    {loading === "subjects"
                      ? "Henter emner…"
                      : needsTable
                        ? "Vælg emne"
                        : "Alle emner"}
                  </option>
                  {subjectGroups.map((group) => (
                    <optgroup key={group.subject_group_id} label={group.subject_group_name}>
                      {group.subjects.map((subject) => (
                        <option key={subject.subject_id} value={subject.subject_id}>
                          {subject.subject_name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
            )}

            {needsTable && (
              <label className="field">
                <span>Tabel</span>
                <select
                  value={tableId}
                  onChange={(event) => {
                    setTableId(event.target.value);
                    setSettingsError(null);
                  }}
                  disabled={!subjectId || loading === "tables"}
                >
                  <option value="">
                    {loading === "tables"
                      ? "Henter tabeller…"
                      : subjectId
                        ? "Vælg tabel"
                        : "Vælg først et emne"}
                  </option>
                  {tables.map(({ groupName, table }) => (
                    <option key={table.table_id} value={table.table_id}>
                      {groupName} · {table.table_name} ({table.table_id})
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {settingsError && (
            <InlineError title="Indstillingerne kunne ikke hentes" message={settingsError} />
          )}

          {loading === "metadata" && <Loading label="Henter tabelmetadata…" />}

          {endpoint === "data" && metadata && (
            <div className="data-settings">
              <div className="selected-table">
                <strong>{metadata.table_name}</strong>
                <code>{metadata.table_id}</code>
              </div>

              <SelectionGroup title="Målingsvariable" count={selectedCount(selections.measures)}>
                <PickerActions
                  onAll={() =>
                    setSelections((current) => ({
                      ...current,
                      measures: Object.fromEntries(
                        metadata.mgroups.map((group) => [
                          group.mgroup_id,
                          group.measures.map((item) => item.measure_id),
                        ]),
                      ),
                    }))
                  }
                  onClear={() =>
                    setSelections((current) => ({ ...current, measures: {} }))
                  }
                  clearDisabled={selectedCount(selections.measures) === 0}
                />
                {metadata.mgroups.map((group) => (
                  <MeasurePicker
                    key={group.mgroup_id}
                    group={group}
                    selected={selections.measures[group.mgroup_id] ?? []}
                    onChange={(values) =>
                      setSelections((current) => ({
                        ...current,
                        measures: { ...current.measures, [group.mgroup_id]: values },
                      }))
                    }
                  />
                ))}
              </SelectionGroup>

              <SelectionGroup title="Perioder" count={selectedCount(selections.periods)}>
                {metadata.periods.map((period) => (
                  <PeriodPicker
                    key={period.periodtype_id}
                    period={period}
                    selected={selections.periods[period.periodtype_id] ?? []}
                    onChange={(values) =>
                      setSelections((current) => ({
                        ...current,
                        periods: { ...current.periods, [period.periodtype_id]: values },
                      }))
                    }
                  />
                ))}
              </SelectionGroup>

              <SelectionGroup title="Dimensioner" count={selectedCount(selections.hierarchyValues)}>
                {metadata.dimensions.map((dimension) => (
                  <DimensionPicker
                    key={dimension.dimension_id}
                    dimension={dimension}
                    hierarchyId={selections.hierarchyByDimension[dimension.dimension_id] ?? ""}
                    selectedValues={selections.hierarchyValues}
                    onHierarchyChange={(hierarchyId) =>
                      setSelections((current) => ({
                        ...current,
                        hierarchyByDimension: {
                          ...current.hierarchyByDimension,
                          [dimension.dimension_id]: hierarchyId,
                        },
                      }))
                    }
                    onValuesChange={(hierarchyId, values) =>
                      setSelections((current) => ({
                        ...current,
                        hierarchyValues: {
                          ...current.hierarchyValues,
                          [hierarchyId]: values,
                        },
                      }))
                    }
                  />
                ))}
              </SelectionGroup>

            </div>
          )}
        </Step>

        <Step number="3" title="Kald API'et">
          <RequestPreview request={request} />
          <div className="actions">
            <p className="actions-help">
              {endpoint === "data" ? (
                <>
                  <strong>Udfør</strong> sender forespørgslen og viser svaret i trin 4.{" "}
                  <strong>Valider og estimer</strong> er valgfri og sender forespørgslen til{" "}
                  <code>/plan</code>, som kontrollerer den og svarer med et estimat af rækker,
                  kolonner og celler uden at hente data. Svaret vises også i trin 4.
                </>
              ) : (
                <>
                  <strong>Udfør</strong> sender forespørgslen og viser svaret i trin 4.
                </>
              )}
            </p>
            <div className="action-buttons">
              {endpoint === "data" && (
                <button
                  className="secondary"
                  type="button"
                  onClick={() =>
                    metadata && runRequest("plan", buildPlanRequest(metadata.table_id, dataRequest))
                  }
                  disabled={!ready || busy !== null}
                >
                  {busy === "plan" ? "Validerer…" : "Valider og estimer"}
                </button>
              )}
              <button
                className="primary"
                type="button"
                onClick={() => runRequest("execute", request)}
                disabled={!ready || busy !== null}
              >
                {busy === "execute" ? "Udfører…" : "Udfør"}
              </button>
            </div>
          </div>
        </Step>

        <Step number="4" title="Se svaret">
          {result ? (
            <div className={result.ok ? "result" : "result result-error"} role={result.ok ? undefined : "alert"}>
              <div className="result-header">
                <strong>
                  {result.status > 0 ? `${result.status} ${result.statusText}` : result.statusText}
                </strong>
                {result.requestId && <span>Request-id: {result.requestId}</span>}
              </div>
              <pre>{result.body}</pre>
            </div>
          ) : (
            <p className="empty-result">
              Vælg indstillinger og klik på Udfør for at se svaret.
            </p>
          )}
        </Step>
      </main>

      <footer>
        <nav className="footer-links" aria-label="Links">
          <a href={GUIDE_URL} target="_blank" rel="noreferrer">Jobindsats API</a>
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </nav>
        <p>Styrelsen for Arbejdsmarked og Rekruttering</p>
      </footer>
    </div>
  );
}

function Step({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="step">
      <h2><span>{number}</span>{title}</h2>
      <div className="step-content">{children}</div>
    </section>
  );
}

function InlineError({ title, message }: { title: string; message: string }) {
  return (
    <div className="inline-error" role="alert">
      <strong>{title}</strong>
      <span>{message}</span>
    </div>
  );
}

function SelectionGroup({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <details className="selection-group">
      <summary><span>{title}</span><small>{count} valgt</small></summary>
      <div>{children}</div>
    </details>
  );
}

function MeasurePicker({
  group,
  selected,
  onChange,
}: {
  group: MeasureGroup;
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  return (
    <fieldset className="picker">
      <legend>{group.mgroup_name ?? group.mgroup_id} <code>{group.mgroup_id}</code></legend>
      <PickerActions
        onAll={() => onChange(group.measures.map((item) => item.measure_id))}
        onClear={() => onChange([])}
        clearDisabled={selected.length === 0}
      />
      <div className="choices">
        {group.measures.map((measure) => (
          <Checkbox
            key={measure.measure_id}
            checked={selected.includes(measure.measure_id)}
            label={measure.measure_name ?? measure.measure_id}
            code={measure.measure_id}
            onChange={() => onChange(toggle(selected, measure.measure_id))}
          />
        ))}
      </div>
    </fieldset>
  );
}

function PeriodPicker({
  period,
  selected,
  onChange,
}: {
  period: PeriodType;
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const values = period.values.filter((value) =>
    `${value.period_name ?? ""} ${value.period_id}`
      .toLocaleLowerCase("da")
      .includes(query.toLocaleLowerCase("da")),
  );
  // "Seneste periode" writes the API selector latest:1 next to any explicit
  // periods; the API decides whether the combination is allowed. The console
  // offers only N=1 so a first call stays small; the hint tells the user that
  // N is theirs to change in their own call.
  const latestSelected = selected.some((item) => item.startsWith("latest:"));
  function toggleLatest() {
    const explicit = selected.filter((item) => !item.startsWith("latest:"));
    onChange(latestSelected ? explicit : [...explicit, "latest:1"]);
  }
  return (
    <fieldset className="picker">
      <legend>{period.periodtype_name} <code>{period.periodtype_id}</code></legend>
      <div className="picker-tools">
        <label className={latestSelected ? "latest-field checked" : "latest-field"}>
          <input type="checkbox" checked={latestSelected} onChange={toggleLatest} />
          <span>Seneste periode <code>latest:1</code></span>
        </label>
        <input
          type="search"
          placeholder="Filtrer perioder"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <div className="choices scrollable">
        {values.slice(0, 80).map((value) => (
          <Checkbox
            key={value.period_id}
            checked={selected.includes(value.period_id)}
            label={value.period_name ?? value.period_id}
            code={value.period_id}
            onChange={() => onChange(toggle(selected, value.period_id))}
          />
        ))}
      </div>
      {values.length > 80 && <p className="hint">Filtrer for at indsnævre {number(values.length)} perioder.</p>}
      {latestSelected && (
        <p className="hint">
          Konsollen sætter altid <code>latest:1</code>. I dit eget kald kan du skrive fx{" "}
          <code>latest:12</code> for de seneste 12 perioder.
        </p>
      )}
    </fieldset>
  );
}

function DimensionPicker({
  dimension,
  hierarchyId,
  selectedValues,
  onHierarchyChange,
  onValuesChange,
}: {
  dimension: Dimension;
  hierarchyId: string;
  selectedValues: Record<string, string[]>;
  onHierarchyChange: (hierarchyId: string) => void;
  onValuesChange: (hierarchyId: string, values: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  // Every hierarchy the API returns is offered as-is; the API validates the request.
  const hierarchies = dimension.hierarchies;
  const hierarchy = hierarchies.find((item) => item.hierarchy_id === hierarchyId);
  // Displayed in value_id order with numeric-aware comparison, so "/", "/2/",
  // "/2/3/", "/10/" read as a tree instead of the API's name order.
  const values = (
    hierarchy?.levels.flatMap((level) =>
      level.values.map((value) => ({ ...value, levelName: level.level_name })),
    ) ?? []
  ).sort((a, b) => (a.value_id ?? "").localeCompare(b.value_id ?? "", "da", { numeric: true }));
  const selected = hierarchy ? selectedValues[hierarchy.hierarchy_id] ?? [] : [];
  const visible = values.filter((value) =>
    `${value.value_name ?? ""} ${value.value_id ?? ""}`
      .toLocaleLowerCase("da")
      .includes(query.toLocaleLowerCase("da")),
  );

  return (
    <fieldset className="picker">
      <legend>
        {dimension.dimension_name ?? dimension.dimension_id}
        {hierarchy?.is_required && <em>Obligatorisk</em>}
        {hierarchy?.is_selectable === false && <em>Alle værdier</em>}
        <code>{dimension.dimension_id}</code>
      </legend>
      {hierarchies.length > 1 && (
        <label className="hierarchy-field">
          <span>Hierarki</span>
          <select
            value={hierarchyId}
            onChange={(event) => onHierarchyChange(event.target.value)}
          >
            {hierarchies.map((item) => (
              <option key={item.hierarchy_id} value={item.hierarchy_id}>
                {item.hierarchy_name ?? item.hierarchy_id}
              </option>
            ))}
          </select>
        </label>
      )}
      {hierarchy && (
        <>
          <div className="picker-tools">
            <PickerActions
              onAll={() =>
                onValuesChange(
                  hierarchy.hierarchy_id,
                  values.map((item) => item.value_id).filter(isString),
                )
              }
              onClear={() => onValuesChange(hierarchy.hierarchy_id, [])}
              clearDisabled={selected.length === 0}
            />
            <input
              type="search"
              placeholder="Filtrer værdier"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <div className="choices scrollable">
            {visible.slice(0, 100).map((value, index) =>
              value.value_id ? (
                <Checkbox
                  key={`${value.value_id}-${index}`}
                  checked={selected.includes(value.value_id)}
                  label={value.value_name ?? value.value_id}
                  code={value.value_id}
                  meta={value.levelName ?? undefined}
                  onChange={() =>
                    onValuesChange(
                      hierarchy.hierarchy_id,
                      toggle(selected, value.value_id!),
                    )
                  }
                />
              ) : null,
            )}
          </div>
          {visible.length > 100 && <p className="hint">Filtrer for at indsnævre {number(visible.length)} værdier.</p>}
        </>
      )}
    </fieldset>
  );
}

function PickerActions({
  onAll,
  onClear,
  clearDisabled,
}: {
  onAll: () => void;
  onClear: () => void;
  clearDisabled: boolean;
}) {
  return (
    <div className="picker-actions">
      <button type="button" onClick={onAll}>Vælg alle</button>
      <button type="button" onClick={onClear} disabled={clearDisabled}>Ryd</button>
    </div>
  );
}

function Checkbox({
  checked,
  label,
  code,
  meta,
  onChange,
}: {
  checked: boolean;
  label: string;
  code: string;
  meta?: string;
  onChange: () => void;
}) {
  return (
    <label className={checked ? "choice checked" : "choice"}>
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span>{label}<small>{meta ? `${meta} · ` : ""}{code}</small></span>
    </label>
  );
}

function RequestPreview({ request }: { request: ReturnType<typeof buildConsoleRequest> }) {
  const [copied, setCopied] = useState(false);
  const body = request.body ? JSON.stringify(request.body, null, 2) : "";
  const content = `${request.apiUrl}${body ? `\n\n${body}` : ""}`;
  return (
    <div className="request-preview">
      <div>
        <span>{request.method}</span>
        <code>{request.apiUrl}</code>
        <button
          type="button"
          onClick={async () => {
            await copyText(content);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1200);
          }}
        >
          {copied ? "Kopieret" : "Kopiér"}
        </button>
      </div>
      {body && <pre>{body}</pre>}
    </div>
  );
}

function Loading({ label }: { label: string }) {
  return <p className="loading"><span />{label}</p>;
}

function flattenTables(subjects: TableSubject[]) {
  return subjects.flatMap((subject) =>
    subject.table_groups.flatMap((group) =>
      group.tables.map((table) => ({ groupName: group.table_group_name, table })),
    ),
  );
}

function selectedCount(values: Record<string, string[]>) {
  return Object.values(values).reduce((sum, selected) => sum + selected.length, 0);
}

function toggle(values: string[], value: string) {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

async function copyText(value: string) {
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    // navigator.clipboard only exists in secure contexts (HTTPS/localhost);
    // fall back to the legacy command for plain-HTTP deployments.
    const helper = document.createElement("textarea");
    helper.value = value;
    helper.style.position = "fixed";
    helper.style.opacity = "0";
    document.body.append(helper);
    helper.select();
    document.execCommand("copy");
    helper.remove();
  }
}

function prettyResponse(value: string, contentType: string | null) {
  if (!contentType?.includes("json")) return value;
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

function isString(value: string | null): value is string {
  return typeof value === "string";
}

function number(value: number) {
  return new Intl.NumberFormat("da-DK").format(value);
}

function errorMessage(cause: unknown) {
  return cause instanceof Error ? cause.message : "Der opstod en uventet fejl.";
}

function handleLoadError(cause: unknown, setter: (message: string) => void) {
  if (cause instanceof DOMException && cause.name === "AbortError") return;
  setter(errorMessage(cause));
}
